import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { ArrowRight, Users, DollarSign, Crown } from "lucide-react";
import SeoHead from "../components/SeoHead";

/**
 * AmbassadorSignup — public self-signup for the Ambassador Program.
 *
 * Zero-approval flow. Creates a role=AMBASSADOR user + active ambassador
 * row at tier=advocate. On success, JWT is stored and we redirect to
 * `/ambassador` (the Back Office).
 *
 * We keep the classic `/ambassadors/apply` route around for people who
 * prefer the guided application → admin approval path, but this is the
 * default CTA on `/ambassadors` and `/ambassadors/apply` from now on.
 */
export default function AmbassadorSignup() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [params] = useSearchParams();
  const refCode = params.get("ref") || "";
  const nav = useNavigate();
  const { refresh } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password || !name) {
      toast.error("Fill every field");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/ambassador/signup", {
        email, password, name, refCode: refCode || null,
      });
      localStorage.setItem("cauldron_token", data.token);
      await refresh();
      toast.success("Welcome to the Ambassador Program");
      nav("/ambassador");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="ambassador-signup-page">
      <SeoHead
        title="Become a Cauldron Ambassador — Instant Signup"
        description="Earn recurring USD + 500 CAUL per practitioner referral. Unlock 1 free year of Practitioner status at 20 paid referrals."
      />
      <div className="max-w-4xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-12 items-start">
        {/* ─── Value prop panel ─── */}
        <div>
          <div className="label-mystic text-secondary mb-3">Ambassador Program</div>
          <h1 className="font-serif text-4xl sm:text-5xl leading-tight">
            Get paid to grow the Cauldron.
          </h1>
          <p className="text-foreground/70 mt-4 leading-relaxed">
            Refer practitioners, earn <strong className="text-secondary">$5–$24.99/month</strong> per
            active subscription plus <strong className="text-secondary">100 CAUL</strong> per
            paid signup. The <strong className="text-secondary">first 20 ambassadors</strong> to
            join get <strong className="text-secondary">1 free year</strong> of the Practitioner tier
            — while keeping every ambassador commission.
          </p>
          <div className="mt-8 space-y-4 text-sm">
            <div className="flex items-start gap-3">
              <Users className="w-4 h-4 text-secondary mt-1 shrink-0" />
              <div><strong className="text-foreground">Instant signup.</strong> No admin approval. Land in your back office in seconds.</div>
            </div>
            <div className="flex items-start gap-3">
              <DollarSign className="w-4 h-4 text-secondary mt-1 shrink-0" />
              <div><strong className="text-foreground">Recurring commissions.</strong> Earn every month a practitioner stays subscribed.</div>
            </div>
            <div className="flex items-start gap-3">
              <Crown className="w-4 h-4 text-secondary mt-1 shrink-0" />
              <div><strong className="text-foreground">Founding slots.</strong> 100 lifetime founding ambassadors — apply through admin for that route.</div>
            </div>
          </div>
        </div>

        {/* ─── Signup form ─── */}
        <form onSubmit={submit} className="parchment p-6 space-y-4" data-testid="ambassador-signup-form">
          <div>
            <label className="label-mystic text-foreground/70">Your Name</label>
            <input
              className="w-full bg-input border border-secondary/25 rounded-sm p-3 mt-1"
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder="How you're known publicly"
              data-testid="signup-name"
            />
          </div>
          <div>
            <label className="label-mystic text-foreground/70">Email</label>
            <input
              type="email"
              className="w-full bg-input border border-secondary/25 rounded-sm p-3 mt-1"
              value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="signup-email"
            />
          </div>
          <div>
            <label className="label-mystic text-foreground/70">Password</label>
            <input
              type="password"
              className="w-full bg-input border border-secondary/25 rounded-sm p-3 mt-1"
              value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Min 8 characters"
              data-testid="signup-password"
            />
          </div>
          {refCode && (
            <div className="text-[11px] text-foreground/60">
              Referred by code <strong className="text-secondary">{refCode}</strong> ↑
            </div>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/85 disabled:opacity-60 py-3 rounded-sm uppercase tracking-[0.18em] text-xs font-semibold inline-flex items-center justify-center gap-2"
            data-testid="signup-submit"
          >
            {submitting ? "Creating your account…" : (<>Create Ambassador Account <ArrowRight className="w-4 h-4" /></>)}
          </button>
          <div className="text-center text-xs text-foreground/55">
            Already an ambassador?{" "}
            <Link to="/auth?mode=signin" className="text-secondary hover:underline">Sign in →</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
